import React, { useEffect, useRef, useState } from "react";

export interface LimitRow {
    tariffCode: string;
    maxAmount?: string;
    maxSingleAmount?: string;
    maxNumber?: string;
    currency?: string;
}

interface Props {
    data?: LimitRow[];
    limitType: "STORE" | "DEVICE";
    readonly?: boolean;
    onChange: (data: LimitRow[]) => void;
}

const STORE_LIMITS: LimitRow[] = [
    {
        tariffCode: "TOTAL_USAGE_MONTHLY_AM_LOCAL",
        currency: "188"
    },
    {
        tariffCode: "TOTAL_USAGE_MONTHLY_AM_USD",
        currency: "840"
    },
    {
        tariffCode: "TOTAL_USAGE_DAILY_LOCAL",
        currency: "188"
    },
    {
        tariffCode: "TOTAL_USAGE_DAILY_USD",
        currency: "840"
    }
];

const DEVICE_LIMITS: LimitRow[] = [
    {
        tariffCode: "TOTAL_USAGE_MONTHLY_COUNT_LOCAL",
        currency: "188"
    },
    {
        tariffCode: "TOTAL_USAGE_MONTHLY_COUNT_USD",
        currency: "840"
    },
    {
        tariffCode: "TR_LIMIT_LOCAL",
        currency: "188"
    },
    {
        tariffCode: "TR_LIMIT_USD",
        currency: "840"
    },
    {
        tariffCode: "DAILY_SAME_CARD"
    }
];

export const LimitsForm: React.FC<Props> = ({
    data = [],
    limitType,
    readonly,
    onChange
}) => {

    const [rows, setRows] = useState<LimitRow[]>([]);

    const isReadonly = readonly === true;
    const initializedRef = useRef(false);


    useEffect(() => {

        if (initializedRef.current) return;

        const today = new Date().toISOString().split("T")[0];

        const baseLimits = limitType === "STORE" ? STORE_LIMITS : DEVICE_LIMITS;

        const initialized = baseLimits.map(limit => {

            const saved = data.find(d => d.tariffCode === limit.tariffCode);

            return {
                tariffCode: limit.tariffCode,
                dateFrom: today,
                currency: limit.currency,
                maxAmount: saved?.maxAmount ?? "",
                maxSingleAmount: saved?.maxSingleAmount ?? "",
                maxNumber: saved?.maxNumber ?? ""
            };

        });

        setRows(initialized);
        onChange(initialized);
        initializedRef.current = true;

    }, [limitType, data]);



    const updateValue = (
        code: string,
        field: keyof LimitRow,
        value: string
    ) => {

        if (isReadonly) return;

        if (field === "maxAmount" || field === "maxSingleAmount") {
            // Solo permitir números y punto decimal
            if (!/^\d*\.?\d*$/.test(value)) return;
        }



        if (field === "maxNumber") {

            if (value.includes("."))
                value = value.replace(".", ""); // Eliminar puntos para números enteros

            if (value.includes(","))
                value = value.replace(",", ""); // Eliminar puntos para números enteros

            value = value == "" ? "" : String(parseInt(value)); // Convertir a entero o vacío
        }



        const updated = rows.map(r =>
            r.tariffCode === code
                ? { ...r, [field]: value }
                : r
        );

        setRows(updated);
        onChange(updated);
    };

    const currencyLabel = (currency?: string) => {
        if (currency === "188") return "CRC";
        if (currency === "840") return "USD";
        return "";
    };

    const getLimitLabel = (code: string) => {
        switch (code) {
            case "TOTAL_USAGE_MONTHLY_AM_LOCAL":
                return "Límite mensual total (CRC)";
            case "TOTAL_USAGE_MONTHLY_AM_USD":
                return "Límite mensual total (USD)";
            case "TOTAL_USAGE_DAILY_LOCAL":
                return "Límite diario total (CRC)";
            case "TOTAL_USAGE_DAILY_USD":
                return "Límite diario total (USD)";
            case "TOTAL_USAGE_MONTHLY_COUNT_LOCAL":
                return "Cantidad máxima de transacciones mensual (CRC)";
            case "TOTAL_USAGE_MONTHLY_COUNT_USD":
                return "Cantidad máxima de transacciones mensual (USD)";
            case "TR_LIMIT_LOCAL":
                return "Límite por transacción (CRC)";
            case "TR_LIMIT_USD":
                return "Límite por transacción (USD)";
            case "DAILY_SAME_CARD":
                return "Cantidad máxima diaria por tarjeta";
            default:
                return code;
        }
    }


    return (
        <div className="mb-4">





            <table className="table table-bordered">

                <thead className="table-light">
                    <tr>
                        <th style={{ width: "40%" }}>Límite</th>
                        <th style={{ width: "20%" }}>Moneda</th>
                        <th style={{ width: "40%" }}>Valor</th>
                    </tr>
                </thead>

                <tbody>

                    {rows.map(row => {

                        let field: keyof LimitRow = "maxAmount";

                        if (row.tariffCode.includes("COUNT") || row.tariffCode === "DAILY_SAME_CARD") {
                            field = "maxNumber";
                        }

                        if (row.tariffCode.includes("TR_LIMIT")) {
                            field = "maxSingleAmount";
                        }

                        return (

                            <tr key={row.tariffCode}>

                                <td className="text-muted">
                                    {getLimitLabel(row.tariffCode)} *
                                </td>

                                <td>
                                    {currencyLabel(row.currency)}
                                </td>

                                <td>

                                    <input
                                        type="number"
                                        step={field === "maxNumber" ? "1" : "0.01"}
                                        min={0}
                                        className="form-control"
                                        value={(row as any)[field] || ""}
                                        required
                                        disabled={isReadonly}
                                        onChange={e =>
                                            updateValue(row.tariffCode, field, e.target.value)
                                        }
                                        onKeyDown={e => {
                                            // Bloquea caracteres no deseados como "-", "+", "e", "."
                                            if (["-", "+", "e", "E", ".", ","].includes(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                    />


                                </td>

                            </tr>

                        );

                    })}

                </tbody>

            </table>



        </div>
    );
};